import { Component, type ErrorInfo, type ReactNode } from "react";
import { RefreshCw } from "lucide-react";
import { Trans, useLocalePath, useTranslation } from "@thyra/landing-i18n";

/**
 * A failed `lazy()` import usually means the user is on an old build whose
 * chunk filenames no longer exist after a deploy. Reloading fixes it, so that
 * case gets its own copy instead of the generic "something broke" message.
 */
function isChunkLoadError(error: Error): boolean {
  return (
    error.name === "ChunkLoadError" ||
    /dynamically imported module|Importing a module script failed|Failed to fetch/i.test(
      error.message,
    )
  );
}

type Props = { children: ReactNode };
type State = { error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Landing render error:", error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return <ErrorFallback stale={isChunkLoadError(error)} />;
  }
}

function ErrorFallback({ stale }: { stale: boolean }) {
  const { t } = useTranslation("common");
  const lp = useLocalePath();
  const copy = stale ? "stale" : "generic";

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 py-16 text-center sm:px-6">
      <span className="eyebrow-pill">{t(`error.${copy}.eyebrow`)}</span>

      <h1 className="mt-6 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
        {t(`error.${copy}.title`)}
      </h1>

      <p className="mt-4 max-w-xl text-base text-muted-foreground sm:text-lg">
        {t(`error.${copy}.body`)}
      </p>

      <div className="mt-8 flex w-full max-w-md flex-col items-stretch gap-3 sm:w-auto sm:max-w-none sm:flex-row sm:items-center">
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="btn-brand group inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl px-7 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 sm:w-auto sm:min-w-[12rem]"
        >
          <RefreshCw
            className="h-4 w-4 transition-transform group-hover:rotate-90"
            strokeWidth={2}
          />
          {t("error.reload")}
        </button>
        {/* Plain anchors, not <Link>. A full navigation also clears the
            boundary's error state, which router navigation would not. */}
        <a
          href={lp("/")}
          className="inline-flex h-12 w-full items-center justify-center rounded-xl border border-border bg-background/80 px-7 text-sm font-semibold text-foreground shadow-sm backdrop-blur-sm transition-colors hover:border-primary/30 hover:bg-primary/5 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 sm:w-auto sm:min-w-[12rem] dark:bg-zinc-900/70"
        >
          {t("error.home")}
        </a>
      </div>

      {!stale && (
        <p className="mt-6 text-sm text-muted-foreground">
          <Trans
            t={t}
            i18nKey="error.stuck"
            components={{
              contact: (
                <a
                  href={lp("/contact")}
                  className="font-medium text-primary underline-offset-4 hover:underline"
                />
              ),
            }}
          />
        </p>
      )}
    </div>
  );
}
